import {
    BLOCK_EXPANDABLE,
    expandable,
    FRAGMENT_EXPANDABLE_BODY,
    FRAGMENT_EXPANDABLE_TITLE,
    isFragmentEmpty,
    moveToNextLeafBlock,
} from '@gitbook/doc-core';
import { type Change, isBlockRangeSelection, TextRange, type Value } from '@gitbook/slate';
import type { NodeProps, Plugin } from '@gitbook/slate-react';

import { isBackspace, isEnter, isModA, isModEnter, selectAllInContainerFirst } from '../common';

export const plugin: Plugin = {
    onKeyDown,
    shouldNodeComponentUpdate,
};

/**
 * Handle key down events for expandable blocks.
 */
function onKeyDown(event: KeyboardEvent, change: Change): Change | undefined {
    const { value } = change;

    if (isBlockRangeSelection(value.selection)) {
        return;
    }

    if (isBackspace(event) && value.isCollapsed && value.selection.anchorOffset === 0) {
        return onBackspaceAtStart(event, change);
    }

    if (isModEnter(event)) {
        return onModEnter(event, change);
    }
    if (isEnter(event)) {
        return onEnter(event, change);
    }
    if (isModA(event)) {
        event.preventDefault();
        return selectAllInContainerFirst(change, getExpandableBodyRange);
    }

    return undefined;
}

function onBackspaceAtStart(_event: KeyboardEvent, change: Change): Change | undefined {
    const current = expandable.getCurrentExpandable(change.value);

    if (!current) {
        return undefined;
    }

    const title = expandable.getTitleFragment(current.expandable);
    const body = expandable.getBodyFragment(current.expandable);

    if (current.fragment === title) {
        if (isFragmentEmpty(title)) {
            // Unwrap the body and move focus to the start
            change.replaceWithDocumentByKey(current.expandable.key, body);
            const firstText = body.getFirstText();
            if (firstText) {
                change.collapseToStartOf(firstText);
            }
            return change;
        }

        // If the title is not empty, normal backspace
        return;
    }

    if (current.fragment === body && change.value.textSelection.isAtStartOf(body)) {
        return change.collapseToEndOf(title);
    }
}

/**
 * Pressing enter in the expandable title, move the focus to the body.
 */
function onEnter(_event: KeyboardEvent, change: Change): Change | undefined {
    const current = expandable.getCurrentExpandable(change.value);

    if (!current) {
        return undefined;
    }

    if (current.fragment?.type === FRAGMENT_EXPANDABLE_TITLE) {
        return change
            .collapseToStartOf(current.expandable.getFragmentByKey(FRAGMENT_EXPANDABLE_BODY)!)
            .focus();
    }
}

/**
 * Pressing mod+enter, exit the expandable block.
 */
function onModEnter(event: KeyboardEvent, change: Change): Change | undefined {
    const { value } = change;

    if (!value.isCollapsed) {
        return;
    }

    const current = expandable.getCurrentExpandable(change.value);
    if (!current) {
        return undefined;
    }

    event.preventDefault();

    moveToNextLeafBlock(change, current.expandable);
    return change;
}

function getExpandableBodyRange(value: Value): TextRange | null {
    const current = expandable.getCurrentExpandable(value);
    if (!current || current.fragment?.type !== FRAGMENT_EXPANDABLE_BODY) {
        return null;
    }

    const body = current.expandable.getFragmentByKey(FRAGMENT_EXPANDABLE_BODY);
    if (!body) {
        return null;
    }
    const firstText = body.getFirstText();
    const lastText = body.getLastText();
    if (!firstText || !lastText) {
        return null;
    }

    return TextRange.create().moveToRangeOf(firstText, lastText);
}

/**
 * Re-rendering an expandable block component if its expandable siblings' state changed.
 * Expandables styles update depending on their previous and next siblings being expandable or not.
 */
function shouldNodeComponentUpdate(props: NodeProps, nextProps: NodeProps): boolean | null {
    if (props.node.type === BLOCK_EXPANDABLE) {
        const { parent: prevParent, node: prevNode } = props;
        const { parent: nextParent, node: nextNode } = nextProps;

        if (prevParent !== nextParent) {
            return true;
        }

        const prevIndex = prevParent.getChildIndex(prevNode.key);
        const nextIndex = nextParent.getChildIndex(nextNode.key);

        if (prevIndex !== nextIndex) {
            return true;
        }

        const prevPrevSiblingType =
            prevIndex > 0 ? prevParent.nodes.get(prevIndex - 1)?.type : null;
        const prevNextSiblingType = prevParent.nodes.get(prevIndex + 1)?.type;
        const prevNodeHasPrevOrNextExpandableSibling =
            prevNode.type === prevPrevSiblingType || prevNode.type === prevNextSiblingType;

        const nextPrevSiblingType =
            nextIndex > 0 ? nextParent.nodes.get(nextIndex - 1)?.type : null;
        const nextNextSiblingType = nextParent.nodes.get(nextIndex + 1)?.type;
        const nextNodeHasPrevOrNextExpandableSibling =
            nextNode.type === nextPrevSiblingType || nextNode.type === nextNextSiblingType;

        if (prevNodeHasPrevOrNextExpandableSibling !== nextNodeHasPrevOrNextExpandableSibling) {
            return true;
        }
    }

    return null;
}
